import { useState, useEffect } from 'react';
import styles from './UserProfile.module.css';
import Toast from "../../generic/toast/Toast";
import UserProfileSection from "../user-profile-section/UserProfileSection";
import UserProfileField from "../user-profile-field/UserProfileField";
import type { User } from "../../types/user/User.tsx";
import UserService from "../../../services/user/UserService";

export default function UserProfile(props: User) {
  const [profile, setProfile] = useState(props.user);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [toast, setToast] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // sync state when props.user arrives asynchronously
  useEffect(() => {
    if (props.user) setProfile(props.user);
  }, [props.user]);

  // guard against null profile
  if (!profile) return null;

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswords((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const showToast = (message, type = 'Success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleProfileSave = (e) => {
    e.preventDefault();
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      showToast('First and last name are required.', 'Error');
      return;
    }
    setProfileLoading(true);
    UserService.updateProfile(profile.first_name, profile.last_name, profile.phone)
        .then(() => {
          setProfileLoading(false);
          showToast('Profile updated successfully.');
        }).catch(() => {
          setProfileLoading(false);
          showToast('Failed to update profile. Please try again.', 'Error');
        });
  };

  const handlePasswordSave = (e) => {
    e.preventDefault();
    if (!passwords.current) {
      showToast('Please enter your current password.', 'Error');
      return;
    }
    if (passwords.next.length < 8) {
      showToast('New password must be at least 8 characters.', 'Error');
      return;
    }
    if (passwords.next !== passwords.confirm) {
      showToast('New passwords do not match.', 'Error');
      return;
    }
    setPasswordLoading(true);

    UserService.updatePassword(passwords.current, passwords.next, passwords.confirm)
        .then(() => {
          setPasswordLoading(false);
          setPasswords({ current: '', next: '', confirm: '' });
          showToast('Password changed successfully.');
        }).catch(() => {
          setPasswordLoading(false);
          showToast('Failed to update password. Please try again.', 'Error');
      });
  };

  return (
      <div className="container">
        <main className={styles.main}>

          {/* ── Page title ── */}
          <div className={styles.pageTop}>
            <div className={styles.avatarLarge}>{profile.user_initials}</div>
            <div>
              <h1 className={styles.pageHeading}>{profile.first_name} {profile.last_name}</h1>
              <p className={styles.pageEmail}>
                <i className="fas fa-lock" style={{ fontSize: 11, marginRight: 6, opacity: 0.5 }} />
                {profile.email}
              </p>
            </div>
          </div>

          <div className={styles.layout}>

            {/* ── Personal details ── */}
            <UserProfileSection
                title="Personal details"
                description="Update your name and phone number."
            >
              <form onSubmit={handleProfileSave} noValidate>
                <div className={styles.row}>
                  <UserProfileField label="First name" id="firstName">
                    <input
                        id="firstName"
                        type="text"
                        className={styles.input}
                        value={profile.first_name ?? ''}
                        onChange={(e) => handleProfileChange('first_name', e.target.value)}
                        autoComplete="given-name"
                    />
                  </UserProfileField>
                  <UserProfileField label="Last name" id="lastName">
                    <input
                        id="lastName"
                        type="text"
                        className={styles.input}
                        value={profile.last_name ?? ''}
                        onChange={(e) => handleProfileChange('last_name', e.target.value)}
                        autoComplete="family-name"
                    />
                  </UserProfileField>
                </div>

                <UserProfileField label="Email address" id="email" hint="Email cannot be changed. Contact support if you need help.">
                  <div className={styles.inputLocked}>
                    <i className="fas fa-lock" />
                    <span>{profile.email}</span>
                  </div>
                </UserProfileField>

                <UserProfileField label="Phone number" id="phone">
                  <input
                      id="phone"
                      type="tel"
                      className={styles.input}
                      value={profile.phone ?? ''}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      autoComplete="tel"
                      placeholder="+1 000 000 0000"
                  />
                </UserProfileField>

                <div className={styles.formFooter}>
                  <button type="submit" className={styles.btnPrimary} disabled={profileLoading}>
                    {profileLoading ? <span className={styles.spinner} /> : 'Save changes'}
                  </button>
                </div>
              </form>
            </UserProfileSection>

            {/* ── Change password ── */}
            <UserProfileSection
                title="Change password"
                description="Use a strong password you don't use anywhere else."
            >
              <form onSubmit={handlePasswordSave} noValidate>
                <UserProfileField label="Current password" id="current">
                  <input
                      id="current"
                      type="password"
                      className={styles.input}
                      value={passwords.current}
                      onChange={(e) => handlePasswordChange('current', e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                  />
                </UserProfileField>

                <UserProfileField label="New password" id="next" hint="Minimum 8 characters.">
                  <input
                      id="next"
                      type="password"
                      className={styles.input}
                      value={passwords.next}
                      onChange={(e) => handlePasswordChange('next', e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                  />
                </UserProfileField>

                <UserProfileField label="Confirm new password" id="confirm">
                  <input
                      id="confirm"
                      type="password"
                      className={styles.input}
                      value={passwords.confirm}
                      onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                  />
                </UserProfileField>

                <div className={styles.formFooter}>
                  <button type="submit" className={styles.btnPrimary} disabled={passwordLoading}>
                    {passwordLoading ? <span className={styles.spinner} /> : 'Update password'}
                  </button>
                </div>
              </form>
            </UserProfileSection>

            {/* ── Notifications ── */}
            <UserProfileSection
                title="Notifications"
                description="Control what emails Code Venture sends you."
            >
              <div className={styles.toggleRow}>
                <div className={styles.toggleInfo}>
                  <p className={styles.toggleLabel}>Course & progress emails</p>
                  <p className={styles.toggleDesc}>
                    Weekly digests, lesson reminders, and milestone celebrations.
                  </p>
                </div>
                <button
                    className={`${styles.toggle} ${profile.email_notifications ? styles.toggleOn : ''}`}
                    onClick={() => setProfile((p) => ({ ...p, email_notifications: !p.email_notifications }))}
                    role="switch"
                    aria-checked={profile.email_notifications}
                >
                  <span className={styles.toggleThumb} />
                </button>
              </div>
              <p className={styles.notifNote}>
                <i className="fas fa-circle-info" style={{ marginRight: 6 }} />
                Transactional emails (password resets, receipts) are always sent regardless of this setting.
              </p>
            </UserProfileSection>

            {/* ── Danger zone ── */}
            <UserProfileSection title="Danger zone">
              <div className={styles.dangerRow}>
                <div>
                  <p className={styles.dangerLabel}>Delete account</p>
                  <p className={styles.dangerDesc}>Permanently remove your account and all your data. This cannot be undone.</p>
                </div>
                <button className={styles.btnDanger}>Delete account</button>
              </div>
            </UserProfileSection>

          </div>
        </main>

        {toast && <Toast message={toast.message} type={toast.type} />}
      </div>
  );
}